import { parseRoofCombinationsFromAi } from './parseRoofCombinationsFromAi';

export const CATALOG_CATEGORY_NAMES = [
    'Max Production with Best Roofs',
    'Best Solar Access',
    'Balanced',
    'Maximum Efficiency',
    'Highest Multiplier',
    'Aesthetic',
] as const;

export type RoofCombination = { name: string; roofFaceIds: string[] };

type ShadeWeight = {
    roofFaceId: string;
    solarAccess?: number;
    roofMultiplier?: number;
    moduleCount?: number;
    actualProduction?: number;
};

/** Map Gemini / heuristic labels to canonical catalog category names. */
export function canonicalCategoryName(name: string): string {
    const n = name.trim().toLowerCase();
    if (n.includes('onedraw') && n.includes('maxfill')) return 'oneDRAW Default Maxfill';
    if (n.includes('max production') || (n.includes('production') && n.includes('best roof'))) {
        return 'Max Production with Best Roofs';
    }
    if (n.includes('solar access')) return 'Best Solar Access';
    if (n.includes('balanced')) return 'Balanced';
    if (n.includes('efficiency')) return 'Maximum Efficiency';
    if (n.includes('multiplier')) return 'Highest Multiplier';
    if (n.includes('aesthetic')) return 'Aesthetic';
    return name.trim();
}

export function roofSetSignature(ids: string[]): string {
    return [...ids].sort().join('|');
}

function productionEstimate(r: ShadeWeight): number {
    if (typeof r.actualProduction === 'number' && Number.isFinite(r.actualProduction)) {
        return r.actualProduction;
    }
    return (r.moduleCount ?? 0) * (r.roofMultiplier ?? 0) * (r.solarAccess ?? 0);
}

/** Build category roof subsets from baseline shade weights when Gemini output is missing or unusable. */
export function buildHeuristicRoofCombinations(
    shadeWeights: ShadeWeight[],
    validIds: Set<string>,
): RoofCombination[] {
    const roofs = shadeWeights.filter(
        (r) => validIds.has(r.roofFaceId) && (r.moduleCount ?? 0) > 0,
    );
    if (!roofs.length) return [];

    const maxRoofs = Math.min(5, roofs.length);
    const minRoofs = Math.max(1, Math.min(2, roofs.length));

    const pickTop = (
        sortFn: (a: ShadeWeight, b: ShadeWeight) => number,
        targetCount = maxRoofs,
    ): string[] => {
        const sorted = [...roofs].sort(sortFn);
        const ids: string[] = [];
        for (const r of sorted) {
            if (ids.includes(r.roofFaceId)) continue;
            ids.push(r.roofFaceId);
            if (ids.length >= targetCount) break;
        }
        if (ids.length >= minRoofs) return ids;
        return roofs.slice(0, minRoofs).map((r) => r.roofFaceId);
    };

    return [
        {
            name: 'Max Production with Best Roofs',
            roofFaceIds: pickTop((a, b) => productionEstimate(b) - productionEstimate(a)),
        },
        {
            name: 'Best Solar Access',
            roofFaceIds: pickTop(
                (a, b) => (b.solarAccess ?? 0) - (a.solarAccess ?? 0),
                Math.min(3, maxRoofs),
            ),
        },
        {
            name: 'Balanced',
            roofFaceIds: pickTop((a, b) => {
                const scoreA = productionEstimate(a) * 0.6 + (a.solarAccess ?? 0) * 100;
                const scoreB = productionEstimate(b) * 0.6 + (b.solarAccess ?? 0) * 100;
                return scoreB - scoreA;
            }),
        },
        {
            name: 'Maximum Efficiency',
            roofFaceIds: pickTop(
                (a, b) => {
                    const effA = (a.solarAccess ?? 0) * (a.roofMultiplier ?? 0);
                    const effB = (b.solarAccess ?? 0) * (b.roofMultiplier ?? 0);
                    return effB - effA;
                },
                Math.min(3, maxRoofs),
            ),
        },
        {
            name: 'Highest Multiplier',
            roofFaceIds: pickTop(
                (a, b) => (b.roofMultiplier ?? 0) - (a.roofMultiplier ?? 0),
                Math.min(3, maxRoofs),
            ),
        },
        {
            name: 'Aesthetic',
            roofFaceIds: pickTop(
                (a, b) => (b.solarAccess ?? 0) * 1000 - (a.moduleCount ?? 0),
                Math.min(2, maxRoofs),
            ),
        },
    ].filter((c) => c.roofFaceIds.length > 0);
}

/**
 * Merge Gemini roof combinations with heuristic fallbacks.
 * Ensures each catalog category has a roof set and prefers distinct subsets when possible.
 */
export function resolveRoofCombinationsForRunAi(
    aiResultText: string,
    validIds: Set<string>,
    shadeWeights: ShadeWeight[] = [],
): RoofCombination[] {
    const parsedRaw = parseRoofCombinationsFromAi(aiResultText, validIds);
    const parsedByCanonical = new Map<string, RoofCombination>();
    for (const combo of parsedRaw) {
        const canonical = canonicalCategoryName(combo.name);
        if (!parsedByCanonical.has(canonical)) {
            parsedByCanonical.set(canonical, { name: canonical, roofFaceIds: combo.roofFaceIds });
        }
    }

    const heuristics = buildHeuristicRoofCombinations(shadeWeights, validIds);
    const heuristicByName = new Map(heuristics.map((h) => [h.name, h]));
    const usedSigs = new Set<string>();
    const result: RoofCombination[] = [];

    for (const catName of CATALOG_CATEGORY_NAMES) {
        const aiCombo = parsedByCanonical.get(catName);
        const heuristic = heuristicByName.get(catName);
        const candidates = [aiCombo, heuristic].filter(Boolean) as RoofCombination[];

        let chosen: RoofCombination | undefined;
        for (const candidate of candidates) {
            const sig = roofSetSignature(candidate.roofFaceIds);
            if (!usedSigs.has(sig)) {
                chosen = candidate;
                usedSigs.add(sig);
                break;
            }
        }
        if (!chosen) {
            chosen = aiCombo ?? heuristic;
        }
        if (chosen?.roofFaceIds?.length) {
            result.push({ name: catName, roofFaceIds: [...chosen.roofFaceIds] });
        }
    }

    return result;
}

const CATEGORY_MAIN_KEY_PREFIX = '__category_main__:';

type CategoryMainEntry = { roofIds?: string[] };

/**
 * When Gemini / heuristic combos are missing (common for older cache blobs), rebuild roof
 * groups from persisted category winners or saved module layouts.
 */
export function deriveRoofCombinationsFromLayoutCatalog(args: {
    categoryMainLayoutByCategory?: Record<string, CategoryMainEntry> | null;
    layoutModulesLayoutByKey?: Record<string, Array<{ roofFaceId?: string }> | undefined> | null;
}): RoofCombination[] {
    const { categoryMainLayoutByCategory, layoutModulesLayoutByKey } = args;
    const result: RoofCombination[] = [];

    for (const catName of CATALOG_CATEGORY_NAMES) {
        const entry = categoryMainLayoutByCategory?.[catName];
        let roofFaceIds = (entry?.roofIds ?? []).filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
        );
        if (!roofFaceIds.length && layoutModulesLayoutByKey) {
            const layoutKey = `${CATEGORY_MAIN_KEY_PREFIX}${catName}`;
            const layout = layoutModulesLayoutByKey[layoutKey];
            if (Array.isArray(layout)) {
                roofFaceIds = layout
                    .map((row) => row.roofFaceId)
                    .filter((id): id is string => typeof id === 'string' && id.length > 0);
            }
        }
        if (roofFaceIds.length > 0) {
            result.push({ name: catName, roofFaceIds: [...roofFaceIds] });
        }
    }

    return result;
}
